import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Trash2, Calendar, User, DollarSign, Edit, Search, FileText, CheckSquare, Printer } from 'lucide-react';
import { getDailyLogs, deleteDailyLog, getBranches, createConsolidatedInvoice, getCustomers } from '../api';
import { buildNumberLookup } from '../lib/customerSearch';
import { printDocument } from '../lib/printDocument';
import { toast } from '../hooks/use-toast';
import { DateRangePresets } from '../components/DateRangePresets';

const DailyLogsList = () => {
  const navigate = useNavigate();
  const [dailyLogs, setDailyLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [numberOf, setNumberOf] = useState(() => () => undefined);
  const [selectedLogs, setSelectedLogs] = useState(new Set());
  
  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    startDate: '',
    endDate: '',
    branch: '',
    customerName: ''
  });

  useEffect(() => {
    loadDailyLogs();
    loadBranches();
  }, []);

  const loadDailyLogs = async () => {
    try {
      const [logs, customers] = await Promise.all([getDailyLogs(), getCustomers().catch(() => [])]);
      const lookup = buildNumberLookup(customers);
      setNumberOf(() => lookup);
      setDailyLogs(logs);
      setFilteredLogs(logs);
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'לא נניתן לטעון את הרישומים',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches();
      setBranches(data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...dailyLogs];

    // Filter by date range
    if (searchFilters.startDate) {
      filtered = filtered.filter(log => {
        const logDate = new Date(log.date);
        const startDate = new Date(searchFilters.startDate);
        return logDate >= startDate;
      });
    }

    if (searchFilters.endDate) {
      filtered = filtered.filter(log => {
        const logDate = new Date(log.date);
        const endDate = new Date(searchFilters.endDate);
        return logDate <= endDate;
      });
    }

    // Filter by branch
    if (searchFilters.branch && searchFilters.branch !== 'all') {
      filtered = filtered.filter(log => log.branch === searchFilters.branch);
    }

    // Filter by customer name
    if (searchFilters.customerName) {
      filtered = filtered.filter(log =>
        log.customerName.toLowerCase().includes(searchFilters.customerName.toLowerCase())
      );
    }

    setFilteredLogs(filtered);
  }, [dailyLogs, searchFilters]);

  // Apply filters when dailyLogs or searchFilters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const clearFilters = () => {
    setSearchFilters({
      startDate: '',
      endDate: '',
      branch: '',
      customerName: ''
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק רישום זה?')) {
      try {
        await deleteDailyLog(id);
        setDailyLogs(dailyLogs.filter(log => log.id !== id));
        toast({ title: 'הצלחה', description: 'הרישום נמחק בהצלחה' });
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן למחוק את הרישום', variant: 'destructive' });
      }
    }
  };

  const toggleLogSelection = (logId) => {
    setSelectedLogs(prev => {
      const next = new Set(prev);
      if (next.has(logId)) next.delete(logId);
      else next.add(logId);
      return next;
    });
  };

  const getSelectedCustomerName = () => {
    const names = new Set();
    selectedLogs.forEach(id => {
      const log = dailyLogs.find(l => l.id === id);
      if (log) names.add(log.customerName);
    });
    return names;
  };

  const handleCreateConsolidated = async () => {
    const customerNames = getSelectedCustomerName();
    if (customerNames.size > 1) {
      toast({ title: 'שגיאה', description: 'חשבון מרוכז יכול להכיל רישומים של לקוח אחד בלבד', variant: 'destructive' });
      return;
    }
    try {
      const invoice = await createConsolidatedInvoice({ logIds: Array.from(selectedLogs) });
      toast({ title: 'נוצר!', description: `חשבון מרוכז ל-${invoice.customerName} נוצר בהצלחה` });
      setSelectedLogs(new Set());
      navigate(`/consolidated-invoice?id=${invoice.id}`);
    } catch (error) {
      toast({ title: 'שגיאה', description: error.response?.data?.detail || 'לא ניתן ליצור חשבון מרוכז', variant: 'destructive' });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return amount ? `₪${parseFloat(amount).toFixed(2)}` : '-';
  };

  // Group logs by month
  const groupByMonth = (logs) => {
    const grouped = {};
    logs.forEach(log => {
      const date = new Date(log.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('he-IL', { year: 'numeric', month: 'long' });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          monthName,
          logs: [],
          total: { transaction: 0, cost: 0, profit: 0 }
        };
      }
      
      grouped[monthKey].logs.push(log);
      
      // Calculate totals from workItems
      if (log.workItems && Array.isArray(log.workItems)) {
        log.workItems.forEach(item => {
          const quantity = parseFloat(item.quantity) || 0;
          const transactionAmount = parseFloat(item.transactionAmount) || 0;
          const costPrice = parseFloat(item.costPrice) || 0;
          
          grouped[monthKey].total.transaction += quantity * transactionAmount;
          grouped[monthKey].total.cost += quantity * costPrice;
          grouped[monthKey].total.profit += (quantity * transactionAmount) - (quantity * costPrice);
        });
      }
    });
    
    Object.values(grouped).forEach(g => g.logs.sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))));
    // Sort by month (newest first)
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">דווח רישום יומי</h1>
            <p className="text-gray-600">סך הכל {dailyLogs.length} רישומים ({filteredLogs.length} תוצאות)</p>
          </div>
          <Button
            onClick={() => navigate('/reports')}
            variant="outline"
            className="border-2 hover:bg-gray-50 transition-colors"
          >
            <ArrowRight className="ml-2 w-5 h-5" />
            חזור לדווח
          </Button>
        </div>

        {/* Consolidated Invoice Floating Bar */}
        {selectedLogs.size > 0 && (
          <div className="sticky top-4 z-50 mb-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 rounded-xl shadow-2xl flex items-center justify-between" data-testid="consolidated-bar">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-6 h-6" />
              <span className="font-bold text-lg">נבחרו {selectedLogs.size} רישומים</span>
              {getSelectedCustomerName().size === 1 && (
                <span className="text-emerald-100">({[...getSelectedCustomerName()][0]})</span>
              )}
              {getSelectedCustomerName().size > 1 && (
                <span className="text-red-200 text-sm">שים לב: יש יותר מלקוח אחד!</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleCreateConsolidated}
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold"
                data-testid="create-consolidated-btn"
              >
                <FileText className="ml-2 w-5 h-5" />
                צור חשבון מרוכז
              </Button>
              <Button
                onClick={() => setSelectedLogs(new Set())}
                variant="ghost"
                className="text-white hover:bg-emerald-500"
              >
                בטל בחירה
              </Button>
            </div>
          </div>
        )}

        {/* Search Filters */}
        <Card className="shadow-lg mb-8">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <Search className="w-6 h-6" />
              חיפוש ברישומים
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <DateRangePresets
              startDate={searchFilters.startDate}
              endDate={searchFilters.endDate}
              onChange={(startDate, endDate) => setSearchFilters(prev => ({ ...prev, startDate, endDate }))}
            />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="space-y-2">
                <Label className="text-lg font-semibold">תאריך התחלה</Label>
                <Input
                  type="date"
                  value={searchFilters.startDate}
                  onChange={(e) => setSearchFilters({ ...searchFilters, startDate: e.target.value })}
                  className="text-lg"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-lg font-semibold">תאריך סיום</Label>
                <Input
                  type="date"
                  value={searchFilters.endDate}
                  onChange={(e) => setSearchFilters({ ...searchFilters, endDate: e.target.value })}
                  className="text-lg"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-lg font-semibold">סניף</Label>
                <Select value={searchFilters.branch} onValueChange={(value) => setSearchFilters({ ...searchFilters, branch: value })}>
                  <SelectTrigger className="text-lg">
                    <SelectValue placeholder="כל הסניפים" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">כל הסניפים</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.name}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-lg font-semibold">שם לקוח</Label>
                <Input
                  type="text"
                  value={searchFilters.customerName}
                  onChange={(e) => setSearchFilters({ ...searchFilters, customerName: e.target.value })}
                  placeholder="חפש לפי שם לקוח..."
                  className="text-lg"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={clearFilters}
                variant="outline"
                className="border-2 border-gray-300 hover:bg-gray-50"
              >
                נקה סינון
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Daily Logs Table */}
        {filteredLogs.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-16 text-center">
              <p className="text-xl text-gray-500 mb-6">אין רישומים יומיים עדיין</p>
              <Button
                onClick={() => navigate('/daily-log')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              >
                צור רישום ראשון
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {groupByMonth(filteredLogs).map(([monthKey, monthData]) => (
              <Card key={monthKey} className="shadow-2xl border-0">
                <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                  <CardTitle className="text-2xl font-bold">{monthData.monthName}</CardTitle>
                  <p className="text-white/80 text-sm">{monthData.logs.length} רישומים</p>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                          <th className="border border-gray-300 p-3 text-center font-bold w-12">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded"
                              checked={monthData.logs.every(l => selectedLogs.has(l.id))}
                              onChange={(e) => {
                                const next = new Set(selectedLogs);
                                monthData.logs.forEach(l => {
                                  if (e.target.checked) next.add(l.id);
                                  else next.delete(l.id);
                                });
                                setSelectedLogs(next);
                              }}
                            />
                          </th>
                          <th className="border border-gray-300 p-3 text-center font-bold">תאריך</th>
                          <th className="border border-gray-300 p-3 text-center font-bold">שם לקוח</th>
                          <th className="border border-gray-300 p-3 text-center font-bold">פירוט עבודה</th>
                          <th className="border border-gray-300 p-3 text-center font-bold">שם ספק</th>
                          <th className="border border-gray-300 p-3 text-center font-bold">תשלום</th>
                          <th className="border border-gray-300 p-3 text-center font-bold">פירוט שיקים</th>
                          <th className="border border-gray-300 p-3 text-center font-bold">פעולות</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthData.logs.map((log, index) => (
                          <tr 
                            key={log.id} 
                            className={`${selectedLogs.has(log.id) ? 'bg-emerald-50 ring-2 ring-emerald-300 ring-inset' : index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'}`}
                          >
                            <td className="border border-gray-300 p-3 text-center align-top">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded"
                                checked={selectedLogs.has(log.id)}
                                onChange={() => toggleLogSelection(log.id)}
                                data-testid={`select-log-${log.id}`}
                              />
                            </td>
                            <td className="border border-gray-300 p-3 text-center whitespace-nowrap align-top">
                              {formatDate(log.date)}
                            </td>
                            <td className="border border-gray-300 p-3 text-center font-semibold align-top">
                              {numberOf(log.customerName) && <span className="block font-mono text-xs text-gray-500" data-testid={`log-customer-number-${log.id}`}>#{numberOf(log.customerName)}</span>}
                              {log.customerName}
                              {log.consolidatedPaidNote && (
                                <div className="mt-1">
                                  <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold" data-testid={`consolidated-badge-${log.id}`}>
                                    {log.consolidatedPaidNote}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="border border-gray-300 p-3">
                              {log.workItems && log.workItems.length > 0 ? (
                                <table className="w-full border-collapse text-sm">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="border border-gray-200 p-2 text-right">שם חלק</th>
                                      <th className="border border-gray-200 p-2 text-center">כמות</th>
                                      <th className="border border-gray-200 p-2 text-center">מחיר עלות</th>
                                      <th className="border border-gray-200 p-2 text-center">סכום עסקה</th>
                                      <th className="border border-gray-200 p-2 text-center">רווח</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {log.workItems.map((item, idx) => {
                                      // Calculate profit if not present
                                      const quantity = parseFloat(item.quantity) || 0;
                                      const transactionAmount = parseFloat(item.transactionAmount) || 0;
                                      const costPrice = parseFloat(item.costPrice) || 0;
                                      const calculatedProfit = (quantity * transactionAmount) - (quantity * costPrice);
                                      const displayProfit = item.profit !== undefined && item.profit !== null ? item.profit : calculatedProfit;
                                      
                                      return (
                                        <tr key={idx}>
                                          <td className="border border-gray-200 p-2 text-right">{item.partName || '-'}</td>
                                          <td className="border border-gray-200 p-2 text-center">{item.quantity || '0'}</td>
                                          <td className="border border-gray-200 p-2 text-center text-orange-700 font-semibold">
                                            {formatCurrency(item.costPrice)}
                                          </td>
                                          <td className="border border-gray-200 p-2 text-center text-blue-700 font-semibold">
                                            {formatCurrency(quantity * transactionAmount)}
                                          </td>
                                          <td className="border border-gray-200 p-2 text-center text-green-700 font-semibold">
                                            {formatCurrency(displayProfit)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="text-gray-400 text-center">אין פריטים</div>
                              )}
                            </td>
                            <td className="border border-gray-300 p-3 text-center align-top">
                              {log.supplierName || '-'}
                            </td>
                            <td className="border border-gray-300 p-3 text-center align-top">
                              {log.payment || '-'}
                            </td>
                            <td className="border border-gray-300 p-3 text-right max-w-xs align-top">
                              {log.checkDetails ? (
                                <div className="line-clamp-2 bg-yellow-50 p-2 rounded" title={log.checkDetails}>
                                  {log.checkDetails}
                                </div>
                              ) : '-'}
                            </td>
                            <td className="border border-gray-300 p-3 text-center align-top">
                              <div className="flex gap-1 justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => printDocument({
                                    title: 'רישום יומי',
                                    customerName: log.customerName,
                                    customerNumber: numberOf(log.customerName),
                                    date: log.date,
                                    items: (log.workItems || []).map(i => ({ name: i.partName, quantity: i.quantity, price: i.transactionAmount })),
                                    payments: (log.payments || []).filter(p => p.method && p.amount),
                                    vatRate: log.vatRate,
                                    docRef: { type: 'daily_log', recordId: log.id }
                                  })}
                                  className="text-gray-700 hover:bg-gray-100"
                                  title="הדפסה"
                                  data-testid={`print-log-${log.id}`}
                                >
                                  <Printer className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/daily-log?id=${log.id}`)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(log.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {/* Monthly Summary Row */}
                      <tfoot>
                        <tr className="bg-gradient-to-r from-indigo-100 to-blue-100 font-bold">
                          <td colSpan="3" className="border border-gray-300 p-4 text-center text-lg">
                            סיכום חודש {monthData.monthName}
                          </td>
                          <td className="border border-gray-300 p-4">
                            <div className="grid grid-cols-3 gap-2 text-center text-sm">
                              <div>
                                <div className="text-orange-900 font-bold text-lg">{formatCurrency(monthData.total.cost)}</div>
                                <div className="text-xs text-gray-600">מחיר עלות</div>
                              </div>
                              <div>
                                <div className="text-blue-900 font-bold text-lg">{formatCurrency(monthData.total.transaction)}</div>
                                <div className="text-xs text-gray-600">סכום עסקה</div>
                              </div>
                              <div>
                                <div className="text-green-900 font-bold text-lg">{formatCurrency(monthData.total.profit)}</div>
                                <div className="text-xs text-gray-600">רווח</div>
                              </div>
                            </div>
                          </td>
                          <td colSpan="4" className="border border-gray-300 p-4"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyLogsList;